import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { Stack, TableFooter, TablePagination, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { VM_STORAGE_TYPE_OPTIONS } from '../../Configs/VirtualMountConstants';
import { color } from '../../Styles/Color';
import DeleteConfirmationModal from '../DeleteConfirmation/DeleteConfirmationModal';
import DeleteButton from '../UiComponents/DeleteButton';

function createData(name, source, destination, filter, prefetch, id) {
  source = source.length > 15 ? source.substring(0, 15) + "..." : source;
  destination = destination.length > 20 ? destination.substring(0, 20) + "..." : destination;
  return { name, source, destination, filter, prefetch, id };
}

const getIcons = (source) => {

  switch (source.split(":")[0]) {
    case "s3": return VM_STORAGE_TYPE_OPTIONS.find(storage => storage.name === "S3").icon("40px")
    case "az": return VM_STORAGE_TYPE_OPTIONS.find(storage => storage.name === "Azure Blob Storage").icon("40px")
    case "gs": return VM_STORAGE_TYPE_OPTIONS.find(storage => storage.name === "Google Cloud").icon("40px")
    case "sftp": return VM_STORAGE_TYPE_OPTIONS.find(storage => storage.name === "SFTP").icon("40px")
    default: return VM_STORAGE_TYPE_OPTIONS.find(storage => storage.name === "HTTP / HTTPS").icon("40px")
  }
}

export default function VMList({ data, handleDelete, openModal }) {

  const rows = (data || []).map((d) =>
    createData(d.name, d.src, d.dest, d.filter || null, d.prefetch, d.id))

  const [isConfirmedDelete, setIsConfirmedDelete] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  useEffect(() => {
    if (isConfirmedDelete && deletingId) {
      handleDelete(deletingId)
      setDeletingId(null)
    }
  }, [deletingId, handleDelete, isConfirmedDelete])

  const handleModalDelete = () => {
    setIsConfirmedDelete(true)
    setIsDeleteModalOpen(false)
  }

  const [page, setPage] = useState(0)

  const rowsPerPage = 10

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  useEffect(() => {
    if (data?.length < page * rowsPerPage) {
      setPage(0)
    }
  }, [data, page])


  return (
    <TableContainer component={Paper}
      sx={{
        boxShadow: "none",
        maxHeight: "60vh",
        border: "1px solid #E4E4E4",
      }}>
      <Table
        sx={{
          minWidth: 650,
          "& .MuiTableCell-root": {
            fontSize: "14px",
            fontWeight: 500,
            fontFamily: "BDOGroteskMedium"
          }
        }}
        stickyHeader
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ backgroundColor: color.secondaryBackground }} width="20%">Name</TableCell>
            <TableCell sx={{ backgroundColor: color.secondaryBackground }} width="25%" >Source</TableCell>
            <TableCell sx={{ backgroundColor: color.secondaryBackground }} width="25%">Destination</TableCell>
            <TableCell sx={{ backgroundColor: color.secondaryBackground }} width="10%">Filter</TableCell>
            <TableCell sx={{ backgroundColor: color.secondaryBackground }} width="10%">Prefetch</TableCell>
            <TableCell sx={{ backgroundColor: color.secondaryBackground }} width="10%"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {
            rows.length > 0 ?
              rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  hover
                >
                  <TableCell component="th" scope="row" >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <FolderOpenIcon fontSize='small' sx={{ color: color.primary }} />
                      {row.name}
                    </Stack>
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Stack height="30px">
                        {getIcons(row.source)}
                      </Stack>
                      {row.source}
                    </Stack>
                  </TableCell>
                  <TableCell >{row.destination}</TableCell>
                  <TableCell >{row.filter}</TableCell>
                  <TableCell align='center'>
                    {
                      row.prefetch ?
                        <CheckIcon sx={{ fontSize: "20px", color: "#2C974A" }} /> :
                        <ClearIcon sx={{ fontSize: "20px", color: "#FF6B6A" }} />
                    }
                  </TableCell>
                  <TableCell align='center'>
                    <DeleteButton
                      onClick={() => {
                        setIsDeleteModalOpen(true)
                        setDeletingId(row.id)
                      }
                      }
                    />
                  </TableCell>
                </TableRow>
              )) :
              <TableCell colSpan={7} >
                <Stack
                  direction="row"
                  gap={1}
                  height="5vh"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography variant='body2'>
                    No Virtual Mounts added. Please click on
                  </Typography>
                  <Typography variant='body2' color={color.primary}
                    onClick={openModal}
                    sx={{
                      "&:hover": {
                        cursor: "pointer"
                      }
                    }}
                  >
                    Add Virtual Mount
                  </Typography>
                  <Typography variant='body2'>
                    to add virtual mounts.
                  </Typography>
                </Stack>
              </TableCell>
          }
        </TableBody>
        <TableFooter
          sx={{
            left: 0,
            bottom: 0,
            zindex: 2,
            position: 'sticky'
          }}
        >
          <TableRow sx={{ backgroundColor: color.secondaryBackground }}>
            <TablePagination
              rowsPerPageOptions={[]}
              colSpan={7}
              count={rows?.length || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                native: true,
              }}
              onPageChange={handleChangePage}
              style={{
                borderTop: "1px solid #E4E4E4",
              }}
            />
          </TableRow>
        </TableFooter>
      </Table>
      <DeleteConfirmationModal
        handleDelete={handleModalDelete} itemName="Virtual Mount"
        open={isDeleteModalOpen} setOpen={setIsDeleteModalOpen}
      />
    </TableContainer>
  );
}